import React, { useEffect, useState } from 'react';
import { Users, Loader2, RefreshCw, Search, X, CheckCircle2, Eye, FileText, Inbox, ChevronLeft, ChevronRight } from 'lucide-react';
import { submissionsApi, SubmissionRecord, newslettersApi, NewsletterRecord, CreatorFilter } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
/** Parse Form_Payload JSON safely */
function parsePayload(raw: string): Record<string, any> | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
/** Extract email from payload */
function extractEmail(payload: Record<string, any> | null): string {
  if (!payload) return '-';
  return payload.email || payload.Email || payload.EMAIL || payload.e_mail || '-';
}
/** Extract a display name from payload */
function extractName(payload: Record<string, any> | null): string {
  if (!payload) return '';
  const first = payload.firstName || payload.first_name || payload.FirstName || payload.name || '';
  const last = payload.lastName || payload.last_name || payload.LastName || '';
  return [first, last].filter(Boolean).join(' ');
}
const PAGE_SIZE = 200;
export function Subscriptions() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [newsletters, setNewsletters] = useState<NewsletterRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionRecord | null>(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showPagination = totalCount > PAGE_SIZE;
  const paginationPanel = showPagination ? <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3" data-id="element-6476">
      <div className="text-sm text-gray-600" data-id="element-6477">
        Showing {(currentPage - 1) * PAGE_SIZE + 1}–
        {Math.min(currentPage * PAGE_SIZE, totalCount)} of{' '}
        {totalCount.toLocaleString()} confirmed subscribers
      </div>
      <div className="flex items-center gap-2" data-id="element-6478">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6479">
          <ChevronLeft className="h-4 w-4" data-id="element-6480" />
          Previous
        </button>
        <span className="text-sm text-gray-700 px-2" data-id="element-6481">
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6482">
          Next
          <ChevronRight className="h-4 w-4" data-id="element-6483" />
        </button>
      </div>
    </div> : null;
  const buildScopeFilters = (): CreatorFilter[] => {
    const filters: CreatorFilter[] = [];
    filters.push({
      field: 'Team',
      value: currentWorkspace.name
    });
    if (currentSubRegion) {
      filters.push({
        field: 'Region',
        value: currentSubRegion
      });
    }
    // Always filter to Confirmed only on this page
    filters.push({
      field: 'Status',
      value: 'Confirmed'
    });
    return filters;
  };
  const fetchData = async (page: number = 1, search: string = activeSearch) => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const scopeFilters = buildScopeFilters();
      // Search filter (multi-field)
      const searchFilter: CreatorFilter[] = search.trim() ? [{
        fields: ['Subscriber_Email', 'search_text'],
        value: search.trim()
      }] : [];
      const dataFilters: CreatorFilter[] = [...scopeFilters, ...searchFilter];
      const [subsResult, nlResult] = await Promise.all([submissionsApi.list({
        offset: offset > 0 ? offset : undefined,
        filters: dataFilters
      }), newslettersApi.list()]);
      setSubmissions(subsResult.data);
      setTotalCount(subsResult.total_count);
      setNewsletters(nlResult);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };
  const handleSearch = () => {
    const trimmed = searchQuery.trim();
    setActiveSearch(trimmed);
    fetchData(1, trimmed);
  };
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    fetchData(1, '');
  };
  useEffect(() => {
    fetchData(1, activeSearch);
  }, [currentWorkspace.name, currentSubRegion]);
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchData(page);
  };
  const getNewsletterName = (newsletterId: string): string => {
    const nl = newsletters.find(n => n.Newsletter_ID === newsletterId);
    return nl?.Newsletter || newsletterId || '-';
  };
  return <div className="space-y-6" data-id="element-6484">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-6485">
        <div className="min-w-0" data-id="element-6486">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6487">
            Subscriptions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6488">
            Confirmed subscribers who completed double opt-in.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-6489">
          <button onClick={() => fetchData(currentPage)} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-6490">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6491" />
          </button>
        </div>
      </div>

      {/* Search & count */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-6492">
        <div className="flex gap-2 flex-1 max-w-sm" data-id="element-6493">
          <div className="relative flex-1" data-id="element-6494">
            <input type="text" placeholder="Search by email, name, or newsletter..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') handleSearch();
          }} className="pl-3 pr-8 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-6495" />
            {searchQuery && <button onClick={handleClearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-6496">
                <X className="h-3.5 w-3.5" data-id="element-6497" />
              </button>}
          </div>
          <button onClick={handleSearch} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" title="Search" data-id="element-6498">
            <Search className="h-4 w-4" data-id="element-6499" />
          </button>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500" data-id="element-6500">
          <CheckCircle2 className="h-4 w-4 text-green-500" data-id="element-6501" />
          <span data-id="element-6502">
            {totalCount.toLocaleString()} confirmed subscriber
            {totalCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Top Pagination */}
      {paginationPanel}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6503">
        {loading && submissions.length === 0 ? <div className="p-12 text-center" data-id="element-6504">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-6505" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-6506">
              Loading subscriptions...
            </p>
          </div> : submissions.length === 0 ? <div className="p-12 text-center" data-id="element-6507">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-6508" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-6509">
              {activeSearch ? 'No matching subscribers' : 'No confirmed subscribers yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-6510">
              {activeSearch ? 'Try adjusting your search.' : 'Subscribers who confirm their double opt-in email will appear here.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-6511">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-6512">
              <thead className="bg-gray-50" data-id="element-6513">
                <tr data-id="element-6514">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6515">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6516">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6517">
                    Newsletter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6518">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6519">
                    Confirmed At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6520">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6521">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6522">
                {submissions.map(sub => {
              const payload = parsePayload(sub.Form_Payload);
              const email = extractEmail(payload);
              const name = extractName(payload);
              return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-6523">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6524">
                        <span className="text-sm font-medium text-gray-900 truncate block max-w-[220px]" data-id="element-6525">
                          {email}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6526">
                        <span className="text-sm text-gray-600 truncate block max-w-[150px]" data-id="element-6527">
                          {name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6528">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600 truncate max-w-[180px]" data-id="element-6529">
                          <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-6530" />
                          {getNewsletterName(sub.Newsletter_ID)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6531">
                        {sub.Region || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6532">
                        {sub.Confirmed_At1 ? new Date(sub.Confirmed_At1).toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6533">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-6534">
                          <CheckCircle2 className="h-3 w-3" data-id="element-6535" />
                          Confirmed
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-6536">
                        <button onClick={() => setSelectedSubmission(sub)} className="text-blue-600 hover:text-blue-900 text-sm inline-flex items-center gap-1" data-id="element-6537">
                          <Eye className="h-3.5 w-3.5" data-id="element-6538" />
                          View
                        </button>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Bottom Pagination */}
      {paginationPanel}

      {/* Payload Modal */}
      {selectedSubmission && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6539">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6540">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedSubmission(null)} data-id="element-6541" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-6542">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-6543">
                <div data-id="element-6544">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-6545">
                    Subscriber Details
                  </h3>
                  <p className="text-xs text-gray-500 font-mono mt-0.5" data-id="element-6546">
                    ID: {selectedSubmission.ID}
                  </p>
                </div>
                <button onClick={() => setSelectedSubmission(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-md" data-id="element-6547">
                  <X className="h-5 w-5" data-id="element-6548" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-6549">
                <div className="flex items-center justify-between" data-id="element-6550">
                  <span className="text-sm font-medium text-gray-700" data-id="element-6551">
                    Status
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-6552">
                    <CheckCircle2 className="h-3 w-3" data-id="element-6553" />
                    Confirmed
                  </span>
                </div>

                <div className="flex items-center justify-between" data-id="element-6554">
                  <span className="text-sm font-medium text-gray-700" data-id="element-6555">
                    Newsletter
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-6556">
                    {getNewsletterName(selectedSubmission.Newsletter_ID)}
                  </span>
                </div>

                {(() => {
              const payload = parsePayload(selectedSubmission.Form_Payload);
              if (!payload) return <div className="text-sm text-red-500" data-id="element-6557">
                        Could not parse payload
                      </div>;
              return <div className="space-y-2" data-id="element-6558">
                      <h4 className="text-sm font-medium text-gray-700" data-id="element-6559">
                        Form Data
                      </h4>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200" data-id="element-6560">
                        {Object.entries(payload).filter(([key]) => key !== 'newsletter_id' && key !== 'submitted_at').map(([key, value]) => <div key={key} className="flex items-center justify-between px-3 py-2" data-id="element-6561">
                              <span className="text-xs font-medium text-gray-500" data-id="element-6562">
                                {key}
                              </span>
                              <span className="text-sm text-gray-900 text-right max-w-[250px] truncate" data-id="element-6563">
                                {String(value)}
                              </span>
                            </div>)}
                      </div>
                      {payload.submitted_at && <div className="flex items-center justify-between pt-2" data-id="element-6564">
                          <span className="text-xs text-gray-400" data-id="element-6565">
                            Submitted at
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-6566">
                            {new Date(payload.submitted_at).toLocaleString()}
                          </span>
                        </div>}
                    </div>;
            })()}

                <details className="group" data-id="element-6567">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600" data-id="element-6568">
                    View raw JSON
                  </summary>
                  <pre className="mt-2 bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto max-h-48" data-id="element-6569">
                    {JSON.stringify(parsePayload(selectedSubmission.Form_Payload), null, 2) || selectedSubmission.Form_Payload}
                  </pre>
                </details>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-6570">
                <button onClick={() => setSelectedSubmission(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6571">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}